import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PromoCard from "../../components/PromoCard/PromoCard";
import PromosApi from "../../api/PromosApi";
import CrystalsApi from "../../api/CrystalsApi";
import bonusApi from "../../api/bonusApi";

const SWIPE_THRESHOLD = 60;

function SwipeableCard({ children, canSwipe, onAction, actionType }) {
    const [offsetX, setOffsetX] = useState(0);
    const [swiped, setSwiped] = useState(false);
    const startX = useRef(0);
    const startY = useRef(0);
    const dragging = useRef(false);
    const isHorizontal = useRef(null);

    const handleTouchStart = useCallback((e) => {
        if (!canSwipe) return;
        startX.current = e.touches[0].clientX;
        startY.current = e.touches[0].clientY;
        dragging.current = true;
        isHorizontal.current = null;
    }, [canSwipe]);

    const handleTouchMove = useCallback((e) => {
        if (!dragging.current || !canSwipe) return;
        const dx = e.touches[0].clientX - startX.current;
        const dy = e.touches[0].clientY - startY.current;

        if (isHorizontal.current === null) {
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
                isHorizontal.current = Math.abs(dx) > Math.abs(dy);
            }
            return;
        }
        if (!isHorizontal.current) return;

        if (swiped) {
            const newOffset = -SWIPE_THRESHOLD + dx;
            setOffsetX(Math.min(0, Math.max(-SWIPE_THRESHOLD, newOffset)));
        } else {
            setOffsetX(Math.min(0, dx));
        }
    }, [canSwipe, swiped]);

    const handleTouchEnd = useCallback(() => {
        dragging.current = false;
        isHorizontal.current = null;
        if (!canSwipe) return;
        if (offsetX < -SWIPE_THRESHOLD / 2) {
            setOffsetX(-SWIPE_THRESHOLD);
            setSwiped(true);
        } else {
            setOffsetX(0);
            setSwiped(false);
        }
    }, [canSwipe, offsetX]);

    const handleAction = useCallback(() => {
        setOffsetX(-300);
        setTimeout(() => {
            onAction?.();
        }, 250);
    }, [onAction]);

    const resetSwipe = useCallback(() => {
        setOffsetX(0);
        setSwiped(false);
    }, []);

    if (!canSwipe) return children;

    return (
        <div className="relative overflow-hidden rounded-[8px]">
            {/* Action button behind the card */}
            <div
                className="absolute right-0 top-0 bottom-0 flex items-center justify-center"
                style={{ width: SWIPE_THRESHOLD + 8, zIndex: 0 }}
            >
                <div
                    onClick={handleAction}
                    className="w-[44px] h-[44px] rounded-full bg-[#EF4444] flex items-center justify-center cursor-pointer active:scale-90 transition-transform"
                    style={{
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </div>
            </div>

            {/* Card content */}
            <div
                style={{
                    transform: `translateX(${offsetX}px)`,
                    transition: dragging.current ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    zIndex: 1,
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={swiped ? resetSwipe : undefined}
            >
                {children}
            </div>
        </div>
    );
}

// Настройки анимации списка
const listVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.15,  // интервал между стартом анимации карточек
            delayChildren: 0.2,     // общая задержка перед первой карточкой
        },
    },
};

// Настройки анимации каждой карточки
const itemVariants = {
    hidden: {
        opacity: 0,
        y: 20,
        scale: 0.95,
    },
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 120,
            damping: 20,
        },
    },
};

// Проверка завершённого промо
const isCompletedPromo = (promo) => 
    promo?.status === 'COMPLETED' || promo?.status === 'MANUALLY_COMPLETED';

export default function PromoList({ onScroll }) {
    // все промо, которые будут показаны (myPromos + subPromos)
    const [promos, setPromos] = useState([]);

    // отдельно держим для дебага, если нужно
    const [myPromos, setMyPromos] = useState([]);
    const [subPromos, setSubPromos] = useState([]);

    const [crystals,setCrystals] = useState([]);

    /** ID промо, созданных как 1st time free (бонус) — показываем бейдж */
    const [bonusPromoIds, setBonusPromoIds] = useState(new Set());
    const [bonusLoading, setBonusLoading] = useState(false);

    // статусы загрузки
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Состояние для archived секции
    const [archivedCollapsed, setArchivedCollapsed] = useState(false);
    
    // Разделяем на активные и архивные
    const { activePromos, archivedPromos } = useMemo(() => {
        const active = promos.filter(p => !isCompletedPromo(p));
        const archived = promos.filter(p => isCompletedPromo(p));
        return { activePromos: active, archivedPromos: archived };
    }, [promos]);


    const fetchPromos = React.useCallback(async (refetchOnly = false) => {
        try {
            if (!refetchOnly) {
                setLoading(true);
                setError('');
            }

            const responseMy = await PromosApi.UserPromos.myPromos();
            const myList = responseMy?.data?.data;
            const safeMyList = Array.isArray(myList) ? myList : [];

            const responseSubs = await PromosApi.UserPromos.subPromos();
            const subsRaw = Array.isArray(responseSubs?.data) ? responseSubs.data : [];
            const subsPromosOnly = subsRaw
                .map(item => item?.promo)
                .filter(Boolean);

            setMyPromos(safeMyList);
            setSubPromos(subsPromosOnly);
            setPromos([...safeMyList, ...subsPromosOnly]);
        } catch (e) {
            console.error('[PromoList] failed to load promos:', e);
            if (!refetchOnly) {
                setError('Не удалось загрузить промо');
                setPromos([]);
                setMyPromos([]);
                setSubPromos([]);
            }
        } finally {
            if (!refetchOnly) setLoading(false);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        fetchPromos().catch(() => {});
        return () => { isMounted = false; };
    }, [fetchPromos]);

    useEffect(() => {
        let cancelled = false;
        setBonusLoading(true);

        // Загружаем список бонусных промо для отображения бейджей
        bonusApi.getMyBonusPromos()
            .then((bonusPromos) => {
                if (cancelled) return;
                const ids = new Set((bonusPromos || []).map((p) => String(p?.id)).filter(Boolean));
                setBonusPromoIds(ids);
            })
            .catch(() => {
                if (!cancelled) setBonusPromoIds(new Set());
            })
            .finally(() => {
                if (!cancelled) setBonusLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    return (
        <div className="mt-[10px] px-[14px] pb-4" onScroll={onScroll}>
            <motion.div
                className="w-full flex flex-col min-h-0 gap-[10px] "
                initial="hidden"
                animate="show"
                variants={listVariants}
            >
                {/* LOADING: показываем 3 карточки-заглушки тем же стилем */}
                {loading && promos.length === 0 && (
                    [...Array(3)].map((_, i) => (
                        <motion.div
                            key={`skeleton-${i}`}
                            variants={itemVariants}
                        >
                            <PromoCard />
                        </motion.div>
                    ))
                )}

                {/* ERROR */}
                {!loading && error && promos.length === 0 && (
                    <div className="text-red-400 text-sm">
                        {error}
                    </div>
                )}

                {/* Активные промо */}
                {!loading && activePromos.length > 0 && activePromos.map((promo, i) => {
                    const isMyPromo = myPromos.some((p) => String(p?.id) === String(promo?.id));
                    const isLive = promo?.status === 'ACTIVE';
                    const canDelete = isMyPromo && !isLive;
                    const canUnsub = !isMyPromo;
                    const canSwipe = canDelete || canUnsub;

                    const handleSwipeAction = async () => {
                        try {
                            if (canDelete) {
                                await PromosApi.delete(promo.id);
                            } else if (canUnsub) {
                                await PromosApi.UserPromos.unsubscribe(promo.id);
                            }
                            fetchPromos(true);
                        } catch (err) {
                            console.error('[PromoList] swipe action failed:', err);
                        }
                    };

                    return (
                        <motion.div
                            key={promo.id || i}
                            variants={itemVariants}
                        >
                            <SwipeableCard
                                canSwipe={canSwipe}
                                onAction={handleSwipeAction}
                                actionType={canDelete ? 'delete' : 'unsub'}
                            >
                                <PromoCard
                                    promo={promo}
                                    isMyPromo={isMyPromo}
                                    onStartSuccess={() => fetchPromos(true)}
                                />
                            </SwipeableCard>
                        </motion.div>
                    );
                })}

                {/* Разделитель Archived */}
                {!loading && archivedPromos.length > 0 && (
                    <motion.div 
                        variants={itemVariants}
                        className="w-full mt-[10px]"
                    >
                        <div 
                            className="flex items-center gap-3 cursor-pointer py-2"
                            onClick={() => setArchivedCollapsed(!archivedCollapsed)}
                        >
                            {/* Линия слева */}
                            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#6B7280]" />
                            
                            {/* Текст и иконка */}
                            <div className="flex items-center gap-2 text-[#6B7280]">
                                <span className="text-[13px] font-medium tracking-wider uppercase">Archived</span>
                                <span className="text-[11px] text-[#6B7280]/60">({archivedPromos.length})</span>
                                <motion.svg 
                                    width="12" 
                                    height="12" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2"
                                    animate={{ rotate: archivedCollapsed ? 0 : 180 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </motion.svg>
                            </div>
                            
                            {/* Линия справа */}
                            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#6B7280]" />
                        </div>
                    </motion.div>
                )}

                {/* Архивные промо (collapsible) */}
                <AnimatePresence>
                    {!loading && !archivedCollapsed && archivedPromos.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="flex flex-col gap-[10px] overflow-hidden"
                        >
                            {archivedPromos.map((promo, i) => {
                                const isMyPromo = myPromos.some((p) => String(p?.id) === String(promo?.id));
                                const canUnsub = !isMyPromo;
                                const handleArchiveAction = async () => {
                                    try {
                                        if (isMyPromo) {
                                            await PromosApi.delete(promo.id);
                                        } else {
                                            await PromosApi.UserPromos.unsubscribe(promo.id);
                                        }
                                        fetchPromos(true);
                                    } catch (err) {
                                        console.error('[PromoList] archive swipe action failed:', err);
                                    }
                                };
                                return (
                                    <motion.div
                                        key={promo.id || `archived-${i}`}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ delay: i * 0.05 }}
                                    >
                                        <SwipeableCard
                                            canSwipe
                                            onAction={handleArchiveAction}
                                            actionType={isMyPromo ? 'delete' : 'unsub'}
                                        >
                                            <PromoCard
                                                promo={promo}
                                                isMyPromo={isMyPromo}
                                                onStartSuccess={() => fetchPromos(true)}
                                            />
                                        </SwipeableCard>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ПУСТО БЕЗ ОШИБКИ */}
                {!loading && !error && promos.length === 0 && (
                    <div className="text-white/50 text-sm">
                        У тебя пока нет промо
                    </div>
                )}
            </motion.div>
        </div>
    );
}
